import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canLeadTasks } from "@/context/AuthContext";
import { storage } from "@/lib/storage";
import { toast } from "sonner";
import { Plus, ListFilter, Check, ChevronDown } from "lucide-react";
import { closesWithoutApproval } from "@/lib/tasks";
import { ACTIVE_ST, KANBAN_COLS, byOrder } from "@/components/tasks/taskUtils";
import { FilterBar } from "@/components/shared/FilterBar";
import { KanbanBoard } from "@/components/tasks/KanbanBoard";
import { DeadlineList } from "@/components/tasks/TaskRows";
import { NewTaskDialog } from "@/components/tasks/NewTaskDialog";
import { useTaskRowActions } from "@/components/tasks/useTaskRowActions";

// Panel „Úkoly" v záložce Události a úkoly zakázky — znovupoužívá KanbanBoard /
// DeadlineList ze stránky Úkoly (žádná duplikace), jen předfiltrované na zakázku.
export function ProjectTasksTab({ projectId, phases, tasks, team, narrow, onReload }) {
  const { user } = useAuth();
  const manager = canLeadTasks(user);   // vedení i projektant (173)
  // Místní zrcadlo pro optimistické tahy (Dnes/Priorita/pořadí) — rodič drží pravdu.
  const [localTasks, setLocalTasks] = useState(tasks);
  useEffect(() => setLocalTasks(tasks), [tasks]);
  const [phaseF, setPhaseF] = useState("all");
  const [panelOpen, setPanelOpen] = useState(false);
  const [q, setQ] = useState("");
  const isMine = (t) => (t.assignees || []).includes(user?.id);
  const isDelegated = (t) => t.created_by === user?.id && !isMine(t);
  // Rozsah jako na stránce Úkoly; vedení otevírá „Vše" (prázdná tabule mate),
  // dělník „Moje" a volbu „all" mu storage nesmí podstrčit.
  const [scope, setScope] = useState(() => {
    const v = storage.get("pp-project-tasks-scope") || (manager ? "all" : "mine");
    return !manager && v === "all" ? "mine" : v;
  });
  const changeScope = (v) => { setScope(v); storage.set("pp-project-tasks-scope", v); };
  const scopeMatch = (t) => (scope === "mine" ? isMine(t) : scope === "delegated" ? isDelegated(t) : true);

  const phaseName = (pid) => (phases.find((p) => p.id === pid) || {}).name || "";
  const phaseBase = useMemo(
    () => (phaseF === "all" ? localTasks : localTasks.filter((t) => t.phase_id === phaseF)),
    [localTasks, phaseF]);
  const qq = q.trim().toLowerCase();
  const matchQ = (t) => !qq || [t.title, t.description, phaseName(t.phase_id),
    ...(t.assignee_users || []).map((u) => u.name)].some((s) => (s || "").toLowerCase().includes(qq));
  const filtered = useMemo(
    () => phaseBase.filter((t) => scopeMatch(t) && matchQ(t)),
    [phaseBase, scope, qq]); // eslint-disable-line
  const activeTasks = useMemo(() => filtered.filter((t) => ACTIVE_ST.includes(t.status)), [filtered]);

  const navIds = narrow
    ? activeTasks.map((t) => t.id)
    : KANBAN_COLS.flatMap((st) => filtered.filter((t) => t.status === st).sort(byOrder)).map((t) => t.id);

  const patchLocal = (tid, patch) => setLocalTasks((arr) => arr.map((x) => (x.id === tid ? { ...x, ...patch } : x)));
  const rowActions = useTaskRowActions({ reload: onReload, patchLocal, navIds });
  const { openTask, swipeProps } = rowActions;

  // Stejná logika jako pages/Tasks.js — jen load() nahrazuje onReload (zakázka).
  const onReorder = (updates) => {
    const prev = localTasks;
    const map = Object.fromEntries(updates.map((u) => [u.id, u.patch]));
    setLocalTasks((arr) => arr.map((x) => (map[x.id] ? { ...x, ...map[x.id] } : x)));
    Promise.all(updates.map((u) => api.patch(`/tasks/${u.id}`, u.patch)))
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); setLocalTasks(prev); });
  };

  const onMove = async (t, target) => {
    if (t.status === target) return;
    const closesNow = closesWithoutApproval(t, user);
    if (target === "Nový" || target === "V řešení") {
      if (t.status === "Schváleno" || t.status === "Hotovo") {
        try { await api.post(`/tasks/${t.id}/reopen`, { status: target }); toast.success("Úkol vrácen mezi rozpracované."); onReload(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onReload(); }
        return;
      }
      try { await api.patch(`/tasks/${t.id}`, { status: target }); onReload(); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onReload(); }
      return;
    }
    if (target === "Hotovo") {
      if (closesNow) { toast.info("Ke schválení nic posílat nemusíš — přetáhni úkol rovnou do sloupce Hotovo."); return; }
      rowActions.completeRow(t);
      return;
    }
    if (target === "Schváleno") {
      if (t.status === "Hotovo") {
        if (t.completion?.photos?.length) { openTask(t.id); return; }
        if (!manager) { toast.info("Schvaluje vedení — pošli úkol ke schválení."); return; }
        try { await api.post(`/tasks/${t.id}/approve`); toast.success("Úkol schválen."); onReload(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onReload(); }
        return;
      }
      if (closesNow) {
        try { await api.post(`/tasks/${t.id}/complete`, { note: "", photos: [] }); toast.success("Úkol hotový."); onReload(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onReload(); }
      } else {
        toast.info("Nejdřív úkol odešli ke schválení.");
      }
    }
  };

  const addTrigger = (
    <NewTaskDialog onCreated={onReload} defaultProjectId={projectId} trigger={
      <button data-testid="kanban-add-task" className="w-full h-10 rounded-[12px] border border-dashed border-border text-sm font-bold text-muted-foreground hover:border-brand hover:text-brand transition-colors flex items-center justify-center gap-1.5"><Plus size={16} /> Přidat úkol</button>
    } />
  );

  const filterRow = (ph) => {
    const on = phaseF === ph.id;
    const n = ph.id === "all" ? localTasks.length : localTasks.filter((t) => t.phase_id === ph.id).length;
    return (
      <button key={ph.id} type="button" data-testid={`phase-filter-${ph.id}`}
        onClick={() => { setPhaseF(ph.id); setPanelOpen(false); }}
        className={`w-full min-h-[42px] px-2.5 rounded-[10px] flex items-center justify-between gap-2 text-sm text-left transition-colors ${
          on ? "bg-brand/10 text-brand font-bold" : "hover:bg-secondary"}`}>
        <span className="truncate">{ph.name}</span>
        <span className="flex items-center gap-1.5 shrink-0">
          <span className={`text-xs tabular-nums ${on ? "" : "text-muted-foreground"}`}>{n}</span>
          {on && <Check size={16} />}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-3" data-testid="project-tasks-tab">
      {/* Kanonická lišta (§4): rozsah · roll-in Fáze · hledání — jako stránka Úkoly */}
      {(() => {
        const cAct = (fn) => phaseBase.filter((t) => fn(t) && ACTIVE_ST.includes(t.status)).length;
        const scopeTabs = [
          { value: "mine", label: "Moje", count: cAct(isMine), testid: "pt-scope-mine" },
          { value: "delegated", label: narrow ? "Zadal" : "Zadal jsem", count: cAct(isDelegated), testid: "pt-scope-delegated" },
          ...(manager ? [{ value: "all", label: "Vše", count: cAct(() => true), testid: "pt-scope-all" }] : []),
        ];
        const chips = [
          ...(phaseF !== "all" ? [{ key: "phase", label: `Fáze: ${phaseName(phaseF)}`, clear: () => setPhaseF("all"), testid: "chip-phase" }] : []),
          ...(q.trim() ? [{ key: "q", label: `Hledání: „${q.trim()}"`, clear: () => setQ(""), testid: "chip-search" }] : []),
        ];
        const scopeActive = localTasks.filter((t) => scopeMatch(t) && ACTIVE_ST.includes(t.status)).length;
        const phaseBtn = phases.length > 0 ? (
          <button type="button" onClick={() => setPanelOpen((o) => !o)} data-testid="phase-filter-btn"
            className="h-11 px-4 rounded-full bg-card border border-border inline-flex items-center gap-2 text-sm font-bold shrink-0">
            <ListFilter size={16} className="text-brand" />
            Fáze: {phaseF === "all" ? "všechny" : phaseName(phaseF)}
            <ChevronDown size={15} className={`text-muted-foreground transition-transform ${panelOpen ? "rotate-180" : ""}`} />
          </button>
        ) : null;
        return (
          <div>
            <FilterBar testid="project-tasks-filter-bar"
              segments={[{ tabs: scopeTabs, value: scope, onChange: changeScope, testid: "pt-scope-toggle" }]}
              middle={phaseBtn}
              search={q} onSearch={setQ} searchPlaceholder="Hledat úkol, člověka, fázi…"
              chips={chips} onClearAll={() => { setPhaseF("all"); setQ(""); }}
              resultLine={`${activeTasks.length} z ${scopeActive} ${scopeActive === 1 ? "úkolu" : "úkolů"}`} />
            {panelOpen && phases.length > 0 && (
              <div className="mt-2 rounded-[18px] bg-background p-1.5" data-testid="phase-filter-panel">
                <div className="bg-card border border-border rounded-[16px] p-1.5 max-w-md space-y-0.5">
                  {[{ id: "all", name: "Všechny fáze" }, ...phases].map(filterRow)}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {narrow && activeTasks.length > 0 && (
        <p className="text-[12px] text-muted-foreground px-1" data-testid="swipe-hint">Tahni doprava → hotovo · doleva → akce · přidrž → smazat</p>
      )}

      {narrow ? (
        <>
          {activeTasks.length === 0
            ? <p className="text-muted-foreground py-8 text-center">Žádné aktivní úkoly na této zakázce.</p>
            : <DeadlineList tasks={activeTasks} mobile onOpen={openTask} onComplete={rowActions.completeRow}
                canComplete={rowActions.canComplete} isDelegated={isDelegated} swipe={swipeProps} />}
          <NewTaskDialog onCreated={onReload} defaultProjectId={projectId} trigger={
            <button data-testid="project-new-task-btn" className="w-full h-12 rounded-[14px] bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2"><Plus size={18} /> Nový úkol</button>
          } />
        </>
      ) : (
        <KanbanBoard tasks={filtered} onMove={onMove} onReorder={onReorder} onOpen={openTask}
          isDelegated={isDelegated} addTrigger={addTrigger} lead={manager}
          secondaryLabel={(t) => phaseName(t.phase_id)} />
      )}

      {rowActions.sheets}
    </div>
  );
}
